

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { isAuthenticated, logout, formatDateTime } from '../../lib/utils';
import { discountsAPI } from '../../lib/api';

export default function DiscountsManagementPage() {
    const navigate = useNavigate();
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        type: 'percentage',
        amount: '',
        startsAt: '',
        endsAt: '',
        active: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/admin/login');
            return;
        }
        fetchDiscounts();
    }, []);

    const fetchDiscounts = async () => {
        try {
            setLoading(true);
            const response = await discountsAPI.getAll();
            setDiscounts(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Error fetching discounts:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleOpenModal = () => {
        setFormData({
            code: '',
            type: 'percentage',
            amount: '',
            startsAt: '',
            endsAt: '',
            active: true,
        });
        setError('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            const discountData = {
                ...formData,
                code: formData.code.toUpperCase().trim(),
                amount: parseFloat(formData.amount),
            };

            // Validation
            if (discountData.type === 'percentage' && (discountData.amount <= 0 || discountData.amount > 100)) {
                setError('Percentage must be between 1 and 100');
                setSubmitting(false);
                return;
            }

            if (discountData.type === 'fixed' && discountData.amount <= 0) {
                setError('Fixed amount must be greater than 0');
                setSubmitting(false);
                return;
            }

            await discountsAPI.create(discountData);
            await fetchDiscounts();
            handleCloseModal();
        } catch (err) {
            setError(err.message || 'Failed to create discount');
        } finally {
            setSubmitting(false);
        }
    };

    const isExpired = (discount) => {
        if (!discount.endsAt) return false;
        return new Date(discount.endsAt) < new Date();
    };

    const isUpcoming = (discount) => {
        if (!discount.startsAt) return false;
        return new Date(discount.startsAt) > new Date();
    };

    const isActive = (discount) => {
        return discount.active && !isExpired(discount) && !isUpcoming(discount);
    };

    const getDiscountStatus = (discount) => {
        if (!discount.active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-700' };
        if (isExpired(discount)) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
        if (isUpcoming(discount)) return { label: 'Upcoming', color: 'bg-yellow-100 text-yellow-700' };
        return { label: 'Active', color: 'bg-green-100 text-green-700' };
    };

    const stats = {
        total: discounts.length,
        active: discounts.filter(d => isActive(d)).length,
        upcoming: discounts.filter(d => isUpcoming(d)).length,
        expired: discounts.filter(d => isExpired(d)).length,
    };

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Navigation */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <Link to="/admin/dashboard" className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                </div>
                                <span className="ml-3 text-xl font-bold text-gray-900">Discounts Management</span>
                            </Link>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900 text-sm">
                                Dashboard
                            </Link>
                            <button onClick={logout} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors">
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Discount Codes</h1>
                    <button
                        onClick={handleOpenModal}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center shadow-lg hover:shadow-xl"
                    >
                        <svg className="w-5 h-5 mr-2">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Discount
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-blue-600">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm">Total Discounts</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-2xl font-bold text-green-600">{stats.active}</h3>
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm">Active Now</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-2xl font-bold text-yellow-600">{stats.upcoming}</h3>
                            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-yellow-600" stroke="currentColor" fill="none">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm">Upcoming</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-2xl font-bold text-red-600">{stats.expired}</h3>
                            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm">Expired</p>
                    </div>
                </div>

                {/* Discounts Grid */}
                {loading ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading discounts...</p>
                    </div>
                ) : discounts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {discounts.map((discount) => {
                            const status = getDiscountStatus(discount);
                            return (
                                <div
                                    key={discount.id}
                                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden"
                                >
                                    {/* Card Header with Status */}
                                    <div className={`p-4 ${status.color} border-b`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-semibold">{status.label}</span>
                                            <svg className="w-5 h-5">
                                                {status.label === 'Active' && (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                )}
                                                {status.label === 'Expired' && (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                )}
                                                {status.label === 'Upcoming' && (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                )}
                                                {status.label === 'Inactive' && (
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                )}
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-6">
                                        {/* Discount Code */}
                                        <div className="mb-4">
                                            <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-mono font-bold rounded-lg text-lg shadow-md">
                                                {discount.code}
                                            </div>
                                        </div>

                                        {/* Discount Value */}
                                        <div className="mb-4">
                                            <div className="flex items-baseline">
                                                <span className="text-4xl font-bold text-gray-900">
                                                    {discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`}
                                                </span>
                                                <span className="ml-2 text-lg text-gray-600">off</span>
                                            </div>
                                            <p className="text-sm text-gray-500 mt-1 capitalize">
                                                {discount.type} discount
                                            </p>
                                        </div>

                                        {/* Dates */}
                                        <div className="space-y-2 border-t pt-4">
                                            {discount.startsAt ? (
                                                <div className="flex items-start text-sm">
                                                    <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <div>
                                                        <span className="text-gray-600">Starts:</span>
                                                        <span className="ml-1 text-gray-900">{formatDateTime(discount.startsAt)}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center text-sm text-green-600">
                                                    <svg className="w-4 h-4 mr-2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Active immediately
                                                </div>
                                            )}

                                            {discount.endsAt ? (
                                                <div className="flex items-start text-sm">
                                                    <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <div>
                                                        <span className="text-gray-600">Ends:</span>
                                                        <span className="ml-1 text-gray-900">{formatDateTime(discount.endsAt)}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center text-sm text-green-600">
                                                    <svg className="w-4 h-4 mr-2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    No expiration date
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-gray-400">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Discount Codes Yet</h3>
                        <p className="text-gray-500 mb-6">Create discount codes to offer special deals to your customers</p>
                        <button
                            onClick={handleOpenModal}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-lg"
                        >
                            Create Your First Discount
                        </button>
                    </div>
                )}
            </main>

            {/* Create Discount Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-8">
                        {/* Modal Header */}
                        <div className="p-6 border-b">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl font-bold text-gray-900">Create Discount Code</h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Discount Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Discount Code *
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                                    required
                                    className="w-full px-4 py-3 border text-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono uppercase text-lg"
                                    placeholder="SUMMER2025"
                                    maxLength="20"
                                    pattern="[A-Z0-9]+"
                                />
                                <p className="text-xs text-gray-500 mt-1">Use letters and numbers only (e.g., SAVE20, WINTER2025)</p>
                            </div>

                            {/* Discount Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Discount Type *
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('type', 'percentage')}
                                        className={`px-4 py-3 border-2 rounded-lg font-medium transition-all ${formData.type === 'percentage'
                                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                                : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                    >
                                        Percentage (%)
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleInputChange('type', 'fixed')}
                                        className={`px-4 py-3 border-2 rounded-lg font-medium transition-all ${formData.type === 'fixed'
                                                ? 'border-blue-600 bg-blue-50 text-blue-700'
                                                : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                    >
                                        Fixed Amount ($)
                                    </button>
                                </div>
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Discount Amount *
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={formData.amount}
                                        onChange={(e) => handleInputChange('amount', e.target.value)}
                                        required
                                        min="0"
                                        max={formData.type === 'percentage' ? '100' : undefined}
                                        step={formData.type === 'percentage' ? '1' : '0.01'}
                                        className="w-full pl-4 pr-12 py-3 border text-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                                        placeholder={formData.type === 'percentage' ? '10' : '50.00'}
                                    />
                                    <span className="absolute right-4 top-3.5 text-gray-500 font-semibold text-lg">
                                        {formData.type === 'percentage' ? '%' : '$'}
                                    </span>
                                </div>
                                {formData.type === 'percentage' && (
                                    <p className="text-xs text-gray-500 mt-1">Enter a value between 1 and 100</p>
                                )}
                            </div>

                            {/* Start Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Start Date (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.startsAt}
                                    onChange={(e) => handleInputChange('startsAt', e.target.value)}
                                    className="w-full px-4 py-3 border text-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave empty for immediate activation</p>
                            </div>

                            {/* End Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    End Date (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formData.endsAt}
                                    onChange={(e) => handleInputChange('endsAt', e.target.value)}
                                    className="w-full px-4 py-3 border text-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
                            </div>

                            {/* Active Toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <label htmlFor="active" className="text-sm font-medium text-gray-900 block">
                                        Activate Discount
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1">Make this discount available for use immediately</p>
                                </div>
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        id="active"
                                        checked={formData.active}
                                        onChange={(e) => handleInputChange('active', e.target.checked)}
                                        className="sr-only"
                                    />
                                    <label
                                        htmlFor="active"
                                        className={`block w-14 h-8 rounded-full cursor-pointer transition-colors ${formData.active ? 'bg-blue-600' : 'bg-gray-300'
                                            }`}
                                    >
                                        <span
                                            className={`block w-6 h-6 mt-1 ml-1 rounded-full bg-white shadow-md transform transition-transform ${formData.active ? 'translate-x-6' : ''
                                                }`}
                                        />
                                    </label>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                                    <svg className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-medium text-red-800">Error</p>
                                        <p className="text-sm text-red-700 mt-1">{error}</p>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 px-6 py-3 border text-gray-500 border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors shadow-lg disabled:cursor-not-allowed"
                                >
                                     {submitting ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" fill="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Creating...
                                        </span>
                                    ) : (
                                        'Create Discount'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}